import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import DeclarationCard from '../../components/cultivar/DeclarationCard';
import DeclarationForm from '../../components/cultivar/DeclarationForm';
import {
  ClipboardList, Plus, ChevronLeft, ChevronRight, Loader2, Filter, X, Calendar
} from 'lucide-react';
import type { ProductionDeclaration, Pagination, RegionalLicense } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';
import { useAuth } from '../../context/AuthContext';

function DeclarationManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [declarations, setDeclarations] = useState<ProductionDeclaration[]>([]);
  const [myLicenses, setMyLicenses] = useState<RegionalLicense[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const statusFilter = searchParams.get('status') || '';
  const seasonFilter = searchParams.get('season') || '';
  const currentPage = Number(searchParams.get('page')) || 1;
  const licenseIdFilter = searchParams.get('license_id');

  const loadDeclarations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [declarationsRes, licensesRes] = await Promise.all([
        cultivarService.getMyDeclarations({
          status: statusFilter || undefined,
          season_year: seasonFilter ? Number(seasonFilter) : undefined,
          page: currentPage,
          limit: 12,
        }),
        cultivarService.getMyLicenses({ status: 'active', limit: 100 }),
      ]);

      if (declarationsRes.success && declarationsRes.data) {
        setDeclarations(declarationsRes.data.declarations);
        setPagination(declarationsRes.data.pagination);
      } else {
        setError(declarationsRes.message || 'Failed to load declarations');
      }

      if (licensesRes.success && licensesRes.data) {
        setMyLicenses(licensesRes.data.licenses);
      }
    } catch (err) {
      console.error('Error loading declarations:', err);
      setError('Failed to load declarations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, seasonFilter, currentPage]);

  useEffect(() => {
    loadDeclarations();
  }, [loadDeclarations]);

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    setSearchParams(params, { replace: true });
  };

  const handleAcknowledge = async (declarationId: number, notes?: string) => {
    try {
      const response = await cultivarService.acknowledgeDeclaration(declarationId, notes);
      if (response.success) {
        loadDeclarations();
      } else {
        alert(response.message || 'Failed to acknowledge declaration');
      }
    } catch (err) {
      console.error('Error acknowledging declaration:', err);
      alert('Failed to acknowledge declaration');
    }
  };

  const handleQuery = async (declarationId: number, message: string) => {
    try {
      const response = await cultivarService.queryDeclaration(declarationId, message);
      if (response.success) {
        loadDeclarations();
      } else {
        alert(response.message || 'Failed to query declaration');
      }
    } catch (err) {
      console.error('Error querying declaration:', err);
      alert('Failed to query declaration');
    }
  };

  const handleReject = async (declarationId: number, reason: string) => {
    try {
      const response = await cultivarService.rejectDeclaration(declarationId, reason);
      if (response.success) {
        loadDeclarations();
      } else {
        alert(response.message || 'Failed to reject declaration');
      }
    } catch (err) {
      console.error('Error rejecting declaration:', err);
      alert('Failed to reject declaration');
    }
  };

  const handleCreateDeclaration = async (data: Record<string, unknown>) => {
    setIsSubmitting(true);
    try {
      const response = await cultivarService.submitDeclaration(
        data.license_id as number,
        data
      );
      if (response.success) {
        setShowCreateForm(false);
        loadDeclarations();
      } else {
        alert(response.message || 'Failed to submit declaration');
      }
    } catch (err) {
      console.error('Error submitting declaration:', err);
      alert('Failed to submit declaration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDeclarationUserRole = (declaration: ProductionDeclaration): 'declarant' | 'licensor' => {
    if (declaration.declaring_org_id === user?.organisation_id) return 'declarant';
    return 'licensor';
  };

  const currentYear = new Date().getFullYear();
  const seasonOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'declared', label: 'Declared' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'queried', label: 'Queried' },
    { value: 'revised', label: 'Revised' },
    { value: 'rejected', label: 'Rejected' },
  ];

  // Create Form View
  if (showCreateForm) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Submit Production Declaration"
          subtitle="Declare your production data for a licensed cultivar"
          icon={<ClipboardList className="h-6 w-6 text-white" />}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* License Selection */}
          {!licenseIdFilter && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Select License</CardTitle>
              </CardHeader>
              <CardContent>
                {myLicenses.filter(l => l.licensee_org_id === user?.organisation_id).length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-neutral-600 mb-4">
                      You don't have any active licenses to declare production for.
                    </p>
                    <Button variant="primary" onClick={() => navigate('/cultivar/licenses')}>
                      View Licenses
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {myLicenses
                      .filter(l => l.licensee_org_id === user?.organisation_id)
                      .map((license) => (
                        <button
                          key={license.id}
                          onClick={() => {
                            const params = new URLSearchParams(searchParams);
                            params.set('license_id', String(license.id));
                            setSearchParams(params);
                          }}
                          className="p-4 text-left border border-neutral-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                        >
                          <p className="font-medium text-neutral-900">{license.cultivar_name}</p>
                          <p className="text-sm text-neutral-500">
                            {license.license_tier === 'orchard_planting' ? 'Orchard Planting' : 'Nursery Propagation'} • {license.licensor_org_name}
                          </p>
                        </button>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {licenseIdFilter && (
            <Card>
              <CardContent className="p-6 sm:p-8">
                <DeclarationForm
                  licenseId={Number(licenseIdFilter)}
                  onSubmit={handleCreateDeclaration}
                  onCancel={() => {
                    setShowCreateForm(false);
                    const params = new URLSearchParams(searchParams);
                    params.delete('license_id');
                    setSearchParams(params);
                  }}
                  isSubmitting={isSubmitting}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Production Declarations"
        subtitle="View and manage production declarations for your licensed cultivars"
        icon={<ClipboardList className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="primary" onClick={() => setShowCreateForm(true)}>
            <Plus className="h-4 w-4" />
            Submit Declaration
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 mb-6 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <Filter className="h-4 w-4 text-neutral-400" />
              <span className="text-sm font-medium text-neutral-700">Filters:</span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-neutral-400" />
                <select
                  value={seasonFilter}
                  onChange={(e) => setFilter('season', e.target.value)}
                  className="input-professional py-2 text-sm"
                >
                  <option value="">All Seasons</option>
                  {seasonOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setFilter('status', e.target.value)}
                className="input-professional py-2 text-sm"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {(statusFilter || seasonFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchParams({}, { replace: true })}
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-error-600 mb-4">{error}</p>
              <Button variant="outline" onClick={loadDeclarations}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!loading && !error && declarations.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="h-8 w-8 text-neutral-400" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                No Declarations Found
              </h3>
              <p className="text-neutral-600 mb-6 max-w-md mx-auto">
                {statusFilter || seasonFilter
                  ? 'No declarations match your current filters.'
                  : 'No production declarations have been submitted yet.'}
              </p>
              <Button variant="primary" onClick={() => setShowCreateForm(true)}>
                <Plus className="h-4 w-4" />
                Submit Declaration
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Declaration Grid */}
        {!loading && !error && declarations.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {declarations.map((declaration) => (
                <DeclarationCard
                  key={declaration.id}
                  declaration={declaration}
                  userRole={getDeclarationUserRole(declaration)}
                  onAcknowledge={() => handleAcknowledge(declaration.id)}
                  onQuery={(msg) => handleQuery(declaration.id, msg)}
                  onReject={(reason) => handleReject(declaration.id, reason)}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <span className="px-4 text-sm text-neutral-600">
                  Page {currentPage} of {pagination.pages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === pagination.pages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DeclarationManagement;
